import React, { FC } from 'react'
import { Toggle } from '@moonpig/launchpad-forms'
import { Box, Flex, Text } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import {
  FilterToggle as FilterToggleType,
  InvertedFilterToggle,
} from '../../../../services/types/services'
import { FILTER_ICONS } from '../../utils/FilterIcons'

const ICON_SIZE = 24

const StyledFilterToggle = styled(Flex)`
  cursor: pointer;
  ${s({
    justifyContent: 'space-between',
    alignItems: 'center',
  })};
`

const StyledIconImage = styled.div`
  ${s({
    color: 'colorBlack50',
    width: `${ICON_SIZE}px`,
    height: `${ICON_SIZE}px`,
    mr: 4,
  })}
`

const StyledLabel = styled.label`
  display: flex;
  cursor: pointer;
  ${s({
    color: 'colorTextLabel',
  })}
`
const DesktopLabel = styled.span`
  display: block;

  @media (max-width: 430px) {
    display: none;
  }
`

const MobileLabel = styled.span`
  display: none;

  @media (max-width: 430px) {
    display: block;
  }
`

type Props = {
  /** Filter Toggle object */
  filter: FilterToggleType | InvertedFilterToggle
  /** Aria label for the filter */
  ariaLabel?: string
  /** Whether to show the icon */
  showIcon?: boolean
  /** Whether to render a scaled down version */
  scaledDown?: boolean
  /** Whether the toggle should be tabable */
  noTabable?: boolean
  /** Whether the label text should be bold */
  bold?: boolean
  /** Short label to display on mobile (max-width: 360px) when provided */
  mobileLabel?: string
  /** Function to call when the filter is toggled */
  onChange: (
    filter: FilterToggleType | InvertedFilterToggle,
    select: boolean,
  ) => void
  className?: string
}

const LabelText = ({
  children,
  bold,
}: {
  children: React.ReactNode
  bold: boolean
}) => (
  <Text
    typography={
      bold
        ? { xs: 'typeMobileDisplay05', md: 'typeDesktopBodyLabel' }
        : 'typeBodyText'
    }
  >
    {children}
  </Text>
)

export const FilterToggle: FC<Props> = ({
  filter,
  ariaLabel,
  onChange,
  showIcon = true,
  scaledDown = false,
  noTabable = false,
  bold = true,
  mobileLabel,
  className,
}) => {
  const { icon, label, isSelected, __typename, id } = filter
  const labelIcon = icon && FILTER_ICONS[icon]
  const inverted = __typename === 'FilterInvertedToggle'

  const renderLabelContent = () => {
    if (mobileLabel) {
      return (
        <>
          <DesktopLabel>
            <LabelText bold={bold}>{label}</LabelText>
          </DesktopLabel>
          <MobileLabel>
            <LabelText bold={bold}>{mobileLabel}</LabelText>
          </MobileLabel>
        </>
      )
    }
    return <LabelText bold={bold}>{label}</LabelText>
  }

  return (
    <StyledFilterToggle
      aria-label={ariaLabel || `${label} filter toggle`}
      onClick={() => onChange(filter, !isSelected)}
      data-testid="filter-toggle"
      className={className}
    >
      <StyledLabel htmlFor={id}>
        {labelIcon && showIcon && (
          <StyledIconImage aria-hidden>
            {React.cloneElement(labelIcon, {
              width: ICON_SIZE,
              height: ICON_SIZE,
            })}
          </StyledIconImage>
        )}
        {renderLabelContent()}
      </StyledLabel>

      <Box
        style={{
          scale: scaledDown ? '0.8' : '1',
          // remove this once we have removed the internal x-margins from Launchpad Toggle component
          marginLeft: '-8px',
          marginRight: '-10px',
        }}
      >
        <Toggle
          onChange={() => {}}
          checked={inverted ? !isSelected : !!isSelected}
          value={id}
          id={id}
          tabIndex={noTabable ? -1 : 0}
        />
      </Box>
    </StyledFilterToggle>
  )
}
